import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import StarRating from "../components/StarRating";
import api from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { countryOptions, flagEmoji, regionName, codeFromName } from "../lib/countries";
import { ArrowLeft, Plus, X, Trash2, ExternalLink, Globe, BookOpen, Music2, Skull, Save, CalendarDays, Upload, Loader2 } from "lucide-react";

export default function BandDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [band, setBand] = useState(null);
  const [form, setForm] = useState(null);
  const [newAlbum, setNewAlbum] = useState("");
  const [newMember, setNewMember] = useState({ name: "", instruments: "" });
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [events, setEvents] = useState([]);
  const [showConcert, setShowConcert] = useState(false);
  const [concertMode, setConcertMode] = useState("existing"); // existing | new
  const [concertForm, setConcertForm] = useState({ event_id: "", date: "", rating: 0, comment: "", attended: true });
  const [newEventForm, setNewEventForm] = useState({ name: "", type: "concert", start_date: "", location: "", url: "" });
  const [concertError, setConcertError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/bands/${id}`);
    setBand(data);
    setForm({ ...data });
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/events").then(({ data }) => setEvents(data)); }, []);

  const countries = useMemo(() => countryOptions(lang), [lang]);

  if (!form) return <div className="min-h-screen bg-background"><Navbar /></div>;

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/logo", fd);
      set("logo_url", data.url);
    } finally { setUploading(false); }
  };

  const save = async () => {
    const payload = {
      name: form.name, category: form.category, genre: form.genre, subgenre: form.subgenre,
      country: form.country, country_code: form.country_code, flag: form.flag,
      wikipedia_url: form.wikipedia_url, official_url: form.official_url, deezer_url: form.deezer_url,
      spotify_url: form.spotify_url, apple_music_url: form.apple_music_url,
      youtube_music_url: form.youtube_music_url, bandcamp_url: form.bandcamp_url,
      logo_url: form.logo_url, members: form.members, albums: form.albums, notes: form.notes, rating: form.rating,
    };
    await api.put(`/bands/${id}`, payload);
    setDirty(false);
    load();
  };

  const remove = async () => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/bands/${id}`);
    navigate("/dashboard");
  };

  const openConcert = () => {
    setConcertError("");
    const first = events[0];
    setConcertMode(events.length ? "existing" : "new");
    setConcertForm({ event_id: first?.id || "", date: first?.start_date || "", rating: 0, comment: "", attended: true });
    setNewEventForm({ name: "", type: "concert", start_date: "", location: "", url: "" });
    setShowConcert(true);
  };

  const onSelectEvent = (eventId) => {
    const ev = events.find((e) => e.id === eventId);
    setConcertForm((f) => ({ ...f, event_id: eventId, date: ev?.start_date || "" }));
  };

  const saveConcert = async () => {
    setConcertError("");
    if (concertMode === "new") {
      if (!newEventForm.name.trim() || !newEventForm.start_date) { setConcertError(t("event_name") + " / " + t("start_date")); return; }
      const { data: ev } = await api.post("/events", {
        name: newEventForm.name.trim(), type: newEventForm.type, start_date: newEventForm.start_date,
        end_date: null, location: newEventForm.location, url: newEventForm.url,
      });
      await api.post("/concerts", {
        event_id: ev.id, band_id: id, date: newEventForm.start_date,
        rating: concertForm.rating, comment: concertForm.comment, attended: concertForm.attended,
      });
      const { data: evs } = await api.get("/events");
      setEvents(evs);
      setShowConcert(false);
      load();
      return;
    }
    if (!concertForm.event_id) { setConcertError(t("select_event")); return; }
    const ev = events.find((e) => e.id === concertForm.event_id);
    await api.post("/concerts", {
      event_id: concertForm.event_id, band_id: id,
      date: concertForm.date || ev?.start_date || null,
      rating: concertForm.rating, comment: concertForm.comment, attended: concertForm.attended,
    });
    setShowConcert(false);
    load();
  };

  const inputCls = "w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-white focus:border-primary focus:outline-none";
  const currentCode = (form.country_code || codeFromName(form.country, lang) || "").toUpperCase();
  const changeCountry = (code) => {
    setForm((f) => ({ ...f, country_code: code, country: code ? regionName(code, lang) : "", flag: flagEmoji(code) }));
    setDirty(true);
  };
  const pref = user?.preferred_platform || "deezer";
  const streaming = [
    { key: "deezer", url: form.deezer_url, label: t("deezer") },
    { key: "spotify", url: form.spotify_url, label: t("spotify") },
    { key: "apple_music", url: form.apple_music_url, label: t("apple_music") },
    { key: "youtube_music", url: form.youtube_music_url, label: t("youtube_music") },
    { key: "bandcamp", url: form.bandcamp_url, label: t("bandcamp") },
  ].filter((l) => l.url);
  streaming.sort((a, b) => (b.key === pref ? 1 : 0) - (a.key === pref ? 1 : 0));
  const links = [
    { url: form.official_url, label: t("official_site"), icon: Globe },
    { url: form.wikipedia_url, label: t("wikipedia"), icon: BookOpen },
  ].filter((l) => l.url);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-zinc-400 hover:text-white label-caps text-sm" data-testid="back-btn">
            <ArrowLeft size={16} /> {t("back")}
          </button>
          <div className="flex gap-2">
            {dirty && <button data-testid="save-changes-btn" onClick={save} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground label-caps text-sm rounded-sm font-semibold hover:bg-yellow-400"><Save size={16} /> {t("save")}</button>}
            <button data-testid="delete-band-btn" onClick={remove} className="flex items-center gap-2 px-4 py-2 border border-red-900 text-red-400 label-caps text-sm rounded-sm hover:bg-red-950/40"><Trash2 size={16} /> {t("delete")}</button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-6 mb-8">
          <div className="flex flex-col items-center gap-2">
            <div className="w-40 h-40 shrink-0 bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden flex items-center justify-center">
              {form.logo_url ? <img src={form.logo_url} alt={form.name} className="w-full h-full object-cover" /> : <Skull size={48} className="text-zinc-700" />}
            </div>
            <label data-testid="upload-logo-btn" className="flex items-center gap-2 text-xs label-caps text-zinc-400 hover:text-primary cursor-pointer">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? t("uploading") : t("upload_logo")}
              <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
            </label>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {(flagEmoji(currentCode) || form.flag) && <span className="text-3xl">{flagEmoji(currentCode) || form.flag}</span>}
              <h1 className="heading text-4xl font-bold text-white">{form.name}</h1>
            </div>
            <span className="inline-block label-caps text-xs px-2 py-1 bg-zinc-800 text-primary rounded-sm mb-3">{form.category}</span>
            <p className="text-zinc-300">{form.genre}{form.subgenre ? ` · ${form.subgenre}` : ""}</p>
            {currentCode ? <p className="text-zinc-500 text-sm mt-1">{regionName(currentCode, lang)}</p> : (form.country && <p className="text-zinc-500 text-sm mt-1">{form.country}</p>)}
            <div className="mt-3"><StarRating value={form.rating || 0} onChange={(v) => set("rating", v)} size={24} testid="detail-rating" /></div>
          </div>
        </div>

        <Section title={t("band_info")} className="mb-8">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="label-caps text-xs text-zinc-500">{t("band_name")}</label><input data-testid="edit-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
            <div><label className="label-caps text-xs text-zinc-500">{t("category")}</label>
              <select data-testid="edit-category" value={form.category || "Metal"} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                <option value="Metal">Metal</option>
                <option value="Rock">Rock</option>
              </select>
            </div>
            <div><label className="label-caps text-xs text-zinc-500">{t("genre")}</label><input data-testid="edit-genre" value={form.genre || ""} onChange={(e) => set("genre", e.target.value)} className={inputCls} /></div>
            <div><label className="label-caps text-xs text-zinc-500">{t("subgenre")}</label><input data-testid="edit-subgenre" value={form.subgenre || ""} onChange={(e) => set("subgenre", e.target.value)} className={inputCls} /></div>
            <div><label className="label-caps text-xs text-zinc-500">{t("country")}</label>
              <select data-testid="edit-country" value={currentCode} onChange={(e) => changeCountry(e.target.value)} className={inputCls}>
                <option value="">—</option>
                {countries.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
              </select>
            </div>
            <div><label className="label-caps text-xs text-zinc-500">{t("logo")} URL</label><input data-testid="edit-logo-url" value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className={inputCls} /></div>
          </div>
        </Section>

          {(links.length > 0 || streaming.length > 0) && (
          <div className="flex flex-wrap gap-2 mb-8">
            {links.map((l) => (
              <a key={l.label} href={l.url} target="_blank" rel="noreferrer" data-testid={`link-${l.label}`}
                className="flex items-center gap-2 px-3 py-2 border border-zinc-700 rounded-sm text-sm text-zinc-200 hover:border-primary hover:text-primary transition-colors">
                <l.icon size={16} /> {l.label} <ExternalLink size={12} />
              </a>
            ))}
            {streaming.map((l) => {
              const isPref = l.key === pref;
              return (
                <a key={l.key} href={l.url} target="_blank" rel="noreferrer" data-testid={`stream-${l.key}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors border ${isPref ? "border-primary text-primary bg-primary/10" : "border-zinc-700 text-zinc-200 hover:border-primary hover:text-primary"}`}>
                  <Music2 size={16} /> {l.label} {isPref && <span className="label-caps text-[10px]">★</span>} <ExternalLink size={12} />
                </a>
              );
            })}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <Section title={t("members")}>
            <div className="space-y-2">
              {form.members.map((m, i) => (
                <div key={i} className="flex items-center justify-between bg-black border border-zinc-800 rounded-sm px-3 py-2">
                  <span className="text-sm text-white">{m.name} <span className="text-zinc-500">— {(m.instruments || []).join(", ")}</span></span>
                  <button onClick={() => set("members", form.members.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400"><X size={16} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input data-testid="member-name" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} placeholder={t("name")} className={inputCls} />
                <input data-testid="member-instruments" value={newMember.instruments} onChange={(e) => setNewMember({ ...newMember, instruments: e.target.value })} placeholder={t("instruments")} className={inputCls} />
                <button data-testid="add-member-btn" onClick={() => { if (!newMember.name.trim()) return; set("members", [...form.members, { name: newMember.name.trim(), instruments: newMember.instruments.split(",").map((s) => s.trim()).filter(Boolean) }]); setNewMember({ name: "", instruments: "" }); }}
                  className="px-3 border border-zinc-700 rounded-sm text-primary hover:bg-zinc-800"><Plus size={18} /></button>
              </div>
            </div>
          </Section>

          <Section title={t("albums")}>
            <div className="space-y-2">
              {form.albums.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-black border border-zinc-800 rounded-sm px-3 py-2">
                  <span className="text-sm text-white">{a}</span>
                  <button onClick={() => set("albums", form.albums.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400"><X size={16} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input data-testid="album-input" value={newAlbum} onChange={(e) => setNewAlbum(e.target.value)} placeholder={t("add_album")} className={inputCls} />
                <button data-testid="add-album-btn" onClick={() => { if (!newAlbum.trim()) return; set("albums", [...form.albums, newAlbum.trim()]); setNewAlbum(""); }}
                  className="px-3 border border-zinc-700 rounded-sm text-primary hover:bg-zinc-800"><Plus size={18} /></button>
              </div>
            </div>
          </Section>
        </div>

        <Section title={t("notes")} className="mt-6">
          <textarea data-testid="notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} className={inputCls} />
        </Section>

        <Section title={t("streaming") + " & " + t("links")} className="mt-6">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="label-caps text-xs text-zinc-500">{t("official_site")}</label><input data-testid="edit-official" value={form.official_url || ""} onChange={(e) => set("official_url", e.target.value)} className={inputCls} /></div>
            <div><label className="label-caps text-xs text-zinc-500">{t("wikipedia")}</label><input data-testid="edit-wiki" value={form.wikipedia_url || ""} onChange={(e) => set("wikipedia_url", e.target.value)} className={inputCls} /></div>
            <div><label className="label-caps text-xs text-zinc-500">{t("deezer")}</label><input data-testid="edit-deezer" value={form.deezer_url || ""} onChange={(e) => set("deezer_url", e.target.value)} className={inputCls} /></div>
            <div><label className="label-caps text-xs text-zinc-500">{t("spotify")}</label><input data-testid="edit-spotify" value={form.spotify_url || ""} onChange={(e) => set("spotify_url", e.target.value)} className={inputCls} /></div>
            <div><label className="label-caps text-xs text-zinc-500">{t("apple_music")}</label><input data-testid="edit-apple" value={form.apple_music_url || ""} onChange={(e) => set("apple_music_url", e.target.value)} className={inputCls} /></div>
            <div><label className="label-caps text-xs text-zinc-500">{t("youtube_music")}</label><input data-testid="edit-ytm" value={form.youtube_music_url || ""} onChange={(e) => set("youtube_music_url", e.target.value)} className={inputCls} /></div>
            <div><label className="label-caps text-xs text-zinc-500">{t("bandcamp")}</label><input data-testid="edit-bandcamp" value={form.bandcamp_url || ""} onChange={(e) => set("bandcamp_url", e.target.value)} className={inputCls} /></div>
          </div>
        </Section>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-3 border-b border-zinc-800 pb-2">
            <h2 className="label-caps text-sm text-zinc-400">{t("concerts_seen")}</h2>
            <button data-testid="band-add-concert-btn" onClick={openConcert}
              className="flex items-center gap-2 px-3 py-1.5 text-primary border border-primary/40 label-caps text-xs rounded-sm hover:bg-primary/10">
              <Plus size={14} /> {t("add_concert_here")}
            </button>
          </div>
          {band?.concerts?.length ? (
            <div className="space-y-2">
              {band.concerts.map((c) => (
                <button key={c.id} data-testid="band-concert-item" onClick={() => navigate(`/events/${c.event_id}`)}
                  className="w-full text-left flex items-center justify-between bg-black border border-zinc-800 rounded-sm px-3 py-3 hover:border-zinc-600">
                  <div className="flex items-center gap-3">
                    <CalendarDays size={18} className="text-primary" />
                    <div>
                      <p className="text-white text-sm flex items-center gap-2">
                        {c.event?.name}
                        <span className={`label-caps text-[10px] px-1.5 py-0.5 rounded-sm ${c.attended !== false ? "bg-green-900/40 text-green-400" : "bg-zinc-800 text-zinc-500"}`}>
                          {c.attended !== false ? t("attended_yes") : t("attended_no")}
                        </span>
                      </p>
                      <p className="text-zinc-500 text-xs">{c.date || c.event?.start_date}</p>
                    </div>
                  </div>
                  <StarRating value={c.rating || 0} size={14} testid={`concert-rating-${c.id}`} />
                </button>
              ))}
            </div>
          ) : <p className="text-zinc-500 text-sm">{lang === "fr" ? "Aucun concert vu pour ce groupe." : "No concert seen for this band yet."}</p>}
        </div>
      </main>

      {showConcert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowConcert(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading text-2xl text-white">{t("add_concert")}</h3>
              <button onClick={() => setShowConcert(false)} className="text-zinc-400 hover:text-white"><X size={20} /></button>
            </div>
            {concertError && <div className="mb-3 text-sm text-red-400">{concertError}</div>}
            <div className="flex gap-2 mb-4">
              <button data-testid="concert-mode-existing" onClick={() => setConcertMode("existing")} disabled={events.length === 0}
                className={`flex-1 label-caps text-xs py-2 rounded-sm border transition-colors disabled:opacity-40 ${concertMode === "existing" ? "border-primary text-primary bg-primary/10" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                {t("existing_event")}
              </button>
              <button data-testid="concert-mode-new" onClick={() => setConcertMode("new")}
                className={`flex-1 label-caps text-xs py-2 rounded-sm border transition-colors ${concertMode === "new" ? "border-primary text-primary bg-primary/10" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                {t("new_event")}
              </button>
            </div>
            <div className="space-y-3">
              {concertMode === "new" ? (
                <>
                  <div>
                    <label className="label-caps text-xs text-zinc-400">{t("event_name")}</label>
                    <input data-testid="new-event-name" value={newEventForm.name} onChange={(e) => setNewEventForm({ ...newEventForm, name: e.target.value })} className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label-caps text-xs text-zinc-400">{t("type")}</label>
                      <select data-testid="new-event-type" value={newEventForm.type} onChange={(e) => setNewEventForm({ ...newEventForm, type: e.target.value })} className={inputCls}>
                        <option value="concert">{t("concert")}</option>
                        <option value="festival">{t("festival")}</option>
                      </select>
                    </div>
                    <div>
                      <label className="label-caps text-xs text-zinc-400">{t("start_date")}</label>
                      <input data-testid="new-event-date" type="date" value={newEventForm.start_date} onChange={(e) => setNewEventForm({ ...newEventForm, start_date: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className="label-caps text-xs text-zinc-400">{t("location")}</label>
                    <input data-testid="new-event-location" value={newEventForm.location} onChange={(e) => setNewEventForm({ ...newEventForm, location: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="label-caps text-xs text-zinc-400">{t("event_url")}</label>
                    <input data-testid="new-event-url" type="url" placeholder="https://..." value={newEventForm.url} onChange={(e) => setNewEventForm({ ...newEventForm, url: e.target.value })} className={inputCls} />
                  </div>
                </>
              ) : (
                <div>
                  <label className="label-caps text-xs text-zinc-400">{t("select_event")}</label>
                  <select data-testid="concert-event-select" value={concertForm.event_id} onChange={(e) => onSelectEvent(e.target.value)} className={inputCls}>
                    {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name} — {ev.start_date}</option>)}
                  </select>
                </div>
              )}
              {concertMode === "existing" && (
                <div>
                  <label className="label-caps text-xs text-zinc-400">{t("date")}</label>
                  <input data-testid="concert-date-input" type="date" value={concertForm.date} onChange={(e) => setConcertForm({ ...concertForm, date: e.target.value })} className={inputCls} />
                </div>
              )}
              <div>
                <label className="label-caps text-xs text-zinc-400">{t("attended_label")}</label>
                <div className="flex gap-2 mt-1">
                  <button type="button" data-testid="band-attended-yes" onClick={() => setConcertForm({ ...concertForm, attended: true })}
                    className={`flex-1 label-caps text-xs py-2 rounded-sm border transition-colors ${concertForm.attended ? "border-green-500 text-green-400 bg-green-900/20" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                    {t("attended_yes")}
                  </button>
                  <button type="button" data-testid="band-attended-no" onClick={() => setConcertForm({ ...concertForm, attended: false })}
                    className={`flex-1 label-caps text-xs py-2 rounded-sm border transition-colors ${!concertForm.attended ? "border-primary text-primary bg-primary/10" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                    {t("attended_no")}
                  </button>
                </div>
              </div>
              <div>
                <label className="label-caps text-xs text-zinc-400">{t("rating")}</label>
                <div className="mt-2"><StarRating value={concertForm.rating} onChange={(v) => setConcertForm({ ...concertForm, rating: v })} testid="band-concert-form-rating" /></div>
              </div>
              <div>
                <label className="label-caps text-xs text-zinc-400">{t("comment")}</label>
                <textarea data-testid="concert-comment-input" rows={3} value={concertForm.comment} onChange={(e) => setConcertForm({ ...concertForm, comment: e.target.value })} className={inputCls} />
              </div>
              <button data-testid="band-save-concert-btn" onClick={saveConcert}
                className="w-full bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400 disabled:opacity-60">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, className = "" }) {
  return (
    <div className={className}>
      <h2 className="label-caps text-sm text-zinc-400 mb-3 border-b border-zinc-800 pb-2">{title}</h2>
      {children}
    </div>
  );
}
